import { useQuery } from '@tanstack/react-query';
import type { Building, IlmPerson } from '@map/shared';
import { fetchPerson } from './api.js';

/**
 * Everything Ilm knows about someone on the map, fetched when the card opens.
 * Ilm is the place to change any of it; this only shows it, with a link back.
 */
export function PersonCard({ building }: { building: Building }) {
  const query = useQuery({
    queryKey: ['person', building.id],
    queryFn: () => fetchPerson(building.id),
    staleTime: 5 * 60_000,
  });

  if (query.isPending) return <p className="text-sm text-text-muted">Loading…</p>;
  if (query.isError) return <p className="text-sm text-text-muted">Could not load what Ilm knows.</p>;
  return <PersonDetails person={query.data.person} />;
}

const VERDICT: Record<string, string> = {
  confirmed: 'Confirmed by you',
  verified: 'Checked against sources',
  uncertain: 'Uncertain',
  not_found: 'Not found in sources',
  unverified: 'Not checked yet',
};

function PersonDetails({ person: p }: { person: IlmPerson }) {
  const dates = [p.born && `b. ${p.born}`, p.diedAh && `d. ${p.diedAh} AH${p.diedCe ? ` / ${p.diedCe} CE` : ''}`]
    .filter(Boolean)
    .join(' · ');
  const facts = [p.role, p.era, p.region, p.madhhab].filter(Boolean).join(' · ');

  return (
    <div className="flex flex-col gap-4 text-sm">
      <header>
        <p className="text-base font-medium">
          {p.name}
          {p.prophet && ' ﷺ'}
        </p>
        {p.nameAr && (
          <p dir="rtl" lang="ar" className="text-base">
            {p.nameAr}
          </p>
        )}
        {p.kunya && <p className="text-text-muted">{p.kunya}</p>}
        {facts && <p className="text-text-muted">{facts}</p>}
        {dates && <p className="text-text-muted">{dates}</p>}
        <p className="mt-1 text-xs text-text-muted">
          {VERDICT[p.verification] ?? p.verification}
          {p.familiarity > 0 && ` · familiarity ${p.familiarity}/5`}
        </p>
      </header>

      {p.bio && <p className="leading-relaxed">{p.bio}</p>}
      {p.myNotes && (
        <Block title="My notes">
          <p className="whitespace-pre-wrap">{p.myNotes}</p>
        </Block>
      )}
      {p.aliases.length > 0 && (
        <Block title="Also known as">
          <p className="text-text-muted">{p.aliases.join(' · ')}</p>
        </Block>
      )}
      <Names title="Teachers" names={p.teachers} />
      <Names title="Students" names={p.students} />
      {(p.works.length > 0 || p.books.length > 0) && (
        <Block title="Works">
          <ul className="list-disc pl-5">
            {[...new Set([...p.books.map((b) => b.title), ...p.works])].map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Block>
      )}
      {p.hadiths.length > 0 && (
        <Block title={`Hadiths narrated (${p.hadiths.length})`}>
          <ul className="flex flex-col gap-2">
            {p.hadiths.map((h) => (
              <li key={h.text_en}>
                <p>“{h.text_en}”</p>
                <p className="text-xs text-text-muted">
                  {h.url ? (
                    <a href={h.url} target="_blank" rel="noreferrer" className="underline">
                      {h.collection}
                    </a>
                  ) : (
                    h.collection
                  )}
                  {h.grading && ` · ${h.grading}`}
                </p>
              </li>
            ))}
          </ul>
        </Block>
      )}
      {p.lessons.length > 0 && (
        <Block title={`In your lessons (${p.lessons.length})`}>
          <ul className="flex flex-col gap-2">
            {p.lessons.map((l) => (
              <li key={l.id}>
                <p className="font-medium">{[l.title, l.lesson].filter(Boolean).join(' — ')}</p>
                {l.context && <p className="text-text-muted">{l.context}</p>}
              </li>
            ))}
          </ul>
        </Block>
      )}
      {(p.verifyNote || p.links.length > 0) && (
        <Block title="Sources">
          {p.verifyNote && <p className="text-text-muted">{p.verifyNote}</p>}
          <ul className="mt-1 flex flex-col gap-0.5">
            {p.links.map((l) => (
              <li key={l.url}>
                <a href={l.url} target="_blank" rel="noreferrer" className="underline">
                  {l.title || l.url}
                </a>
              </li>
            ))}
          </ul>
        </Block>
      )}
      <a href={p.url} target="_blank" rel="noreferrer" className="text-xs underline">
        Open in Ilm
      </a>
    </div>
  );
}

function Names({ title, names }: { title: string; names: string[] }) {
  if (names.length === 0) return null;
  return (
    <Block title={title}>
      <p>{names.join(' · ')}</p>
    </Block>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-xs uppercase tracking-wide text-text-muted">{title}</h4>
      {children}
    </section>
  );
}
